//biome-ignore-all lint/suspicious/noExplicitAny: because
import {
  type InfiniteData,
  type UseInfiniteQueryResult,
  useInfiniteQuery,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import {
  type ActivityLogDto,
  type ContactDto,
  type CreateContactDto,
  type CreateCustomerDto,
  CreateFeedbackCommentSchema,
  type CustomerDto,
  type CustomerFilterDto,
  type GetAllParamsDto,
  type GetCustomerContactsResponseDto,
  type GetCustomersResponseDto,
  LifecycleFilter,
  type UpdateContactDto,
  type UpdateCustomerDto,
} from '@tempo-io/fim3-dto';
import type { AxiosInstance } from 'axios';
import type { z } from 'zod';
import { useErrorHandlingMutation } from './useErrorHandlingMutation';
import { getUserDataForResults } from './useFim3Api';

export const CreateFeedbackCommentUISchema = CreateFeedbackCommentSchema.omit({
  commenterId: true,
});
export type CreateFeedbackCommentDto = z.infer<typeof CreateFeedbackCommentUISchema>;

export const useCustomersApi = (api: AxiosInstance) => {
  const baseUrl = 'customers';
  const defaultLimit = 25;

  const getCustomers = async (
    params?: GetAllParamsDto & { filters?: CustomerFilterDto },
  ): Promise<GetCustomersResponseDto> => {
    const { data } = await api.get(baseUrl, { params });

    // Enrich customer data with user display names
    const userIds = data.data
      .map((el: CustomerDto) => el.owner)
      .filter((id?: string | null): id is string => !!id);
    const enrichedData = await getUserDataForResults(
      api,
      userIds,
      data.data,
      (item: any) => item.owner ?? '',
    );

    return { ...data, data: enrichedData };
  };

  const getCustomersByIds = async (ids: number[]): Promise<{ data: CustomerDto[] }> => {
    const { data } = await api.get(`${baseUrl}/getByIds`, { params: { ids } });
    return data;
  };

  const getCustomer = async (id: number): Promise<{ data: CustomerDto }> => {
    const { data } = await api.get(`${baseUrl}/${id}`);
    return data;
  };

  const create = async (dto: CreateCustomerDto): Promise<{ data: CustomerDto }> => {
    const { data } = await api.post(baseUrl, dto);
    return data;
  };

  const update = async (
    dto: UpdateCustomerDto & { id: number },
  ): Promise<{ data: CustomerDto }> => {
    const { data } = await api.put(`${baseUrl}/${dto.id}`, {
      ...dto,
      id: undefined,
    });
    return data;
  };

  const remove = async (id: number) => {
    const { data } = await api.delete(`${baseUrl}/${id}`);
    return data;
  };

  const archiveCustomer = async (id: number) => {
    const { data } = await api.put(`${baseUrl}/${id}/archive`);
    return data;
  };

  const restoreCustomer = async (id: number) => {
    const { data } = await api.put(`${baseUrl}/${id}/restore`);
    return data;
  };

  const contactsQueryKey = 'contacts';

  const getContact = async (customerId: number, id: number): Promise<ContactDto> => {
    const { data } = await api.get(`${baseUrl}/${customerId}/${contactsQueryKey}/${id}`);
    return data;
  };

  const getCustomerContacts = async (
    id: number,
    params?: GetAllParamsDto,
  ): Promise<GetCustomerContactsResponseDto> => {
    const { data } = await api.get(`${baseUrl}/${id}/${contactsQueryKey}`, { params });
    return data;
  };

  const getContactsForCustomers = async (
    customerIds: number[],
    params?: GetAllParamsDto,
  ): Promise<GetCustomerContactsResponseDto> => {
    const { data } = await api.get(`${baseUrl}/${contactsQueryKey}`, {
      params: { ...params, customerIds },
    });
    return data;
  };

  const getAccountContacts = async (
    params?: GetAllParamsDto,
  ): Promise<GetCustomerContactsResponseDto> => {
    const { data } = await api.get(`${baseUrl}/${contactsQueryKey}`, { params });
    return data;
  };
  const getSomeContacts = async (
    contactIds: number[],
    params?: GetAllParamsDto,
  ): Promise<GetCustomerContactsResponseDto> => {
    const { data } = await api.get(`${baseUrl}/getContactsByIds`, {
      params: { ...params, contactIds },
    });
    return data;
  };

  const createContact = async ({
    dto,
    customerId,
  }: {
    dto: CreateContactDto;
    customerId: number;
  }): Promise<ContactDto> => {
    const { data } = await api.post(`${baseUrl}/${customerId}/${contactsQueryKey}`, dto);
    return data;
  };

  const updateContact = async (
    dto: UpdateContactDto & { id: number },
  ): Promise<{ data: ContactDto }> => {
    const { data } = await api.put(`${baseUrl}/${dto.customerId}/${contactsQueryKey}/${dto.id}`, {
      ...dto,
      id: undefined,
    });
    return data;
  };

  const removeContact = async ({ id, customerId }: { customerId: number; id: number }) => {
    const { data } = await api.delete(`${baseUrl}/${customerId}/${contactsQueryKey}/${id}`);
    return data;
  };

  const blockedDomains = async ({ customerId }: { customerId: number }) => {
    const { data } = await api.get(`${baseUrl}/${customerId}/block-domains`);
    return data;
  };

  const blockDomains = async ({
    customerId,
    domains,
  }: {
    customerId: number;
    domains: string[];
  }) => {
    const { data } = await api.post(`${baseUrl}/${customerId}/block-domains`, domains);
    return !!data;
  };

  const unblockDomains = async ({
    customerId,
    domains,
  }: {
    customerId: number;
    domains: string[];
  }) => {
    const { data } = await api.post(`${baseUrl}/${customerId}/unblock-domains`, domains);
    return !!data;
  };

  const getCustomerActivity = async (id: number) => {
    const { data } = await api.get(`${baseUrl}/${id}/activity-logs`);

    const userIds = data.map((el: ActivityLogDto) => el.userId);

    const result = await getUserDataForResults(api, userIds, data, (item: any) => item.userId);

    return result;
  };

  const queryClient = useQueryClient();

  const useGetAll = (
    params?: GetAllParamsDto & { filter?: CustomerFilterDto },
    options?: { enabled?: boolean },
  ) => {
    const limit = params?.limit ?? defaultLimit;
    return useInfiniteQuery({
      initialPageParam: 0,
      gcTime: 0,
      enabled: options?.enabled ?? true,
      queryKey: [baseUrl, 'GetAll', params?.lifecycleFilter, params],
      queryFn: ({ pageParam }) => {
        const newParams = { ...params, limit, offset: pageParam * limit };
        return getCustomers(newParams);
      },
      getNextPageParam: (lastPage) => {
        return lastPage.data.length === limit ? lastPage.pagination.offset / limit + 1 : undefined;
      },
    });
  };

  // Update Customer data in the react-query cache.
  const customersSetCachedData = (customer: CustomerDto) => {
    // update single query cache
    queryClient.setQueryData([baseUrl, customer.id], (oldData: { data: CustomerDto }) => {
      if (oldData?.data?.id === customer.id) {
        oldData.data = { ...oldData.data, ...customer };
      }
      return structuredClone(oldData);
    });

    const infiniteQueryKeys = queryClient
      .getQueryCache()
      .getAll()
      .filter((q) => {
        return q.queryKey[0] === baseUrl && q.queryKey[1] === 'GetAll';
      });

    // update infinite query caches
    infiniteQueryKeys.forEach((key) => {
      queryClient.setQueryData(
        key.queryKey,
        (oldData: InfiniteData<UseInfiniteQueryResult<CustomerDto[]>> | undefined) => {
          if (oldData) {
            pages: for (let i = 0; i < oldData.pages.length; i++) {
              const pageData = oldData.pages[i]?.data || [];
              for (let k = 0; k < pageData.length; k++) {
                if (pageData[k]?.id === customer.id) {
                  pageData[k] = { ...pageData[k], ...customer };
                  break pages;
                }
              }
            }
          }
          return structuredClone(oldData);
        },
      );
    });
  };

  const useGetOne = (id?: number) =>
    useQuery({
      queryKey: [baseUrl, id],
      queryFn: () => getCustomer(id ?? 0),
      enabled: !!id,
    });

  const useGetSome = (ids: number[]) =>
    useQuery({
      queryKey: [baseUrl, ids],
      queryFn: () => getCustomersByIds(ids),
      enabled: ids && ids.length > 0,
    });

  const useGetSomeContacts = (contactIds: number[]) =>
    useQuery({
      queryKey: [contactsQueryKey, contactIds],
      queryFn: () => getSomeContacts(contactIds),
      enabled: contactIds && contactIds.length > 0,
    });
  ``;
  const useGetOneContact = (customerId: number, id: number) =>
    useQuery({
      queryKey: [contactsQueryKey, customerId, id],
      queryFn: () => getContact(customerId, id),
    });

  const useGetContacts = (
    id: number,
    lifecycleFilter: GetAllParamsDto['lifecycleFilter'],
    search?: string,
  ) => {
    return useInfiniteQuery({
      initialPageParam: 0,
      gcTime: 0,
      queryKey: [contactsQueryKey, 'GetAll', id, lifecycleFilter, search],
      queryFn: ({ pageParam }) => {
        const params = {
          limit: defaultLimit,
          offset: pageParam * defaultLimit,
          lifecycleFilter,
          search,
        };
        return getCustomerContacts(id, params);
      },
      getNextPageParam: (lastPage) => {
        return lastPage.data.length === defaultLimit
          ? lastPage.pagination.offset / defaultLimit + 1
          : undefined;
      },
    });
  };

  const useGetContactsForCustomers = (customerIds: number[], search?: string) => {
    return useInfiniteQuery({
      initialPageParam: 0,
      gcTime: 0,
      queryKey: [contactsQueryKey, 'GetAll', customerIds, search],
      queryFn: ({ pageParam }) => {
        const params = {
          limit: defaultLimit,
          offset: pageParam * defaultLimit,
          search,
        };
        return getContactsForCustomers(customerIds, params);
      },
      getNextPageParam: (lastPage) => {
        return lastPage.data.length === defaultLimit
          ? lastPage.pagination.offset / defaultLimit + 1
          : undefined;
      },
    });
  };

  const useSearchAccountContacts = (search: string) =>
    useQuery({
      queryKey: [contactsQueryKey, 'SearchAll', search],
      queryFn: () => getAccountContacts({ limit: defaultLimit, offset: 0, search }),
      enabled: search.trim().length > 0,
    });

  const useGetCustomerActivityLog = (id: number) =>
    useQuery({
      queryKey: [baseUrl, id, 'activity-logs'],
      queryFn: () => getCustomerActivity(id),
    });

  const useCreate = () =>
    useErrorHandlingMutation({
      mutationFn: create,
      onSuccess: () => queryClient.invalidateQueries({ queryKey: [baseUrl] }),
    });

  const useCreateContact = () =>
    useErrorHandlingMutation({
      mutationFn: createContact,
      onSuccess: () => queryClient.invalidateQueries({ queryKey: [contactsQueryKey] }),
    });

  const useUpdate = () =>
    useErrorHandlingMutation({
      mutationFn: update,
      onSuccess: async (data) => {
        customersSetCachedData(data.data);
      },
    });

  const useUpdateContact = () =>
    useErrorHandlingMutation({
      mutationFn: updateContact,
      onSuccess: () => queryClient.invalidateQueries({ queryKey: [contactsQueryKey] }),
    });

  const useRemove = () =>
    useErrorHandlingMutation({
      mutationFn: remove,
      onSuccess: async () => {
        await queryClient.invalidateQueries({ queryKey: [baseUrl] });
        await queryClient.invalidateQueries({ queryKey: ['contacts'] });
        await queryClient.invalidateQueries({ queryKey: ['feedbacks'] });
      },
    });

  const useRemoveContact = () =>
    useErrorHandlingMutation({
      mutationFn: removeContact,
      onSuccess: () => queryClient.refetchQueries({ queryKey: [contactsQueryKey] }),
    });

  const useArchive = () =>
    useErrorHandlingMutation({
      mutationFn: archiveCustomer,
      onSuccess: () =>
        Promise.all([
          queryClient.invalidateQueries({ queryKey: [baseUrl] }),
          queryClient.resetQueries({
            queryKey: [baseUrl, 'GetAll', LifecycleFilter.Archived],
          }),
        ]),
    });

  const useRestore = () =>
    useErrorHandlingMutation({
      mutationFn: restoreCustomer,
      onSuccess: () =>
        Promise.all([
          queryClient.invalidateQueries({ queryKey: [baseUrl] }),
          queryClient.resetQueries({
            queryKey: [baseUrl, 'GetAll', LifecycleFilter.Active],
          }),
        ]),
    });

  const archiveContact = async ({
    contactId,
    customerId,
  }: {
    contactId: number;
    customerId: number;
  }) => {
    const { data } = await api.put(
      `${baseUrl}/${customerId}/${contactsQueryKey}/${contactId}/archive`,
    );
    return data;
  };

  const restoreContact = async ({
    contactId,
    customerId,
  }: {
    contactId: number;
    customerId: number;
  }) => {
    const { data } = await api.put(
      `${baseUrl}/${customerId}/${contactsQueryKey}/${contactId}/restore`,
    );
    return data; // @ts-nocheck
  };

  const useArchiveContact = () =>
    useErrorHandlingMutation({
      mutationFn: archiveContact,
      onSuccess: () => queryClient.invalidateQueries({ queryKey: [contactsQueryKey] }),
    });

  const useRestoreContact = () =>
    useErrorHandlingMutation({
      mutationFn: restoreContact,
      onSuccess: () => queryClient.invalidateQueries({ queryKey: [contactsQueryKey] }),
    });

  const useGetBlockedDomains = (customerId: number) =>
    useQuery({
      queryKey: [baseUrl, 'block-domains'],
      queryFn: () => blockedDomains({ customerId }),
    });

  const useBlockDomains = () =>
    useErrorHandlingMutation({
      mutationFn: blockDomains,
      onSuccess: () => queryClient.refetchQueries({ queryKey: [baseUrl, 'block-domains'] }),
    });

  const useUnblockDomains = () =>
    useErrorHandlingMutation({
      mutationFn: unblockDomains,
      onSuccess: () => queryClient.refetchQueries({ queryKey: [baseUrl, 'block-domains'] }),
    });
  return {
    useGetAll,
    customersSetCachedData,
    useGetOne,
    useGetSome,
    useCreate,
    useUpdate,
    useGetContacts,
    useGetSomeContacts,
    useGetContactsForCustomers,
    useSearchAccountContacts,
    useGetOneContact,
    useGetCustomerActivityLog,
    useCreateContact,
    useRemove,
    useUpdateContact,
    useRemoveContact,
    useRestore,
    useArchive,
    useArchiveContact,
    useRestoreContact,
    useGetBlockedDomains,
    useBlockDomains,
    useUnblockDomains,
  };
};
